import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SECURITY_NOT_IN_ENTITY } from 'common/constants/messages/validations';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { getCashFlowColumns } from '../data/CashFlowColumnConfig';
import { CashFlowParser } from '../data/CashFlowParser';
import { CashFlowReverseParser } from '../data/CashFlowReverseParser';
import { CashFlowRowConfig } from '../data/CashFlowRowConfig';
import { CashFlowColumnAlias } from '../data/constants';
import { UseCashFlowLedgerSpreadsheetConfigProps } from '../data/types';


export const useCashDistributionsSpreadsheetConfig = ({
  ledgerData,
  currency = 'USD',
  isDisabled = false,
  securityOptionsByEntity,
  onDeleteRow,
}: UseCashFlowLedgerSpreadsheetConfigProps) => {
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig<object, object, object, object> | null>(null);

  // Use ref to avoid recreating customValidations on every render
  const securityOptionsByEntityRef = useRef(securityOptionsByEntity);
  useEffect(() => {
    securityOptionsByEntityRef.current = securityOptionsByEntity;
  }, [securityOptionsByEntity]);

  // rowConfig now contains the actual data items (each row is a transaction)
  const rowConfig = useMemo(() => CashFlowRowConfig(ledgerData), [ledgerData]);

  // columns define the fields that will be shown for each row
  const columns = useMemo(() => getCashFlowColumns(currency), [currency]);

  // Stable validation callback that uses ref
  const customValidations = useCallback((params: {
    cell: any;
    cellsToValidate?: any[];
    addFeedbackFromCell: (cell: any, feedback: string, validationType?: string) => void;
    removeFeedbackFromCell: (cell: any, feedback: string, validationType?: string) => void;
  }) => {
    const { cell, cellsToValidate, addFeedbackFromCell, removeFeedbackFromCell } = params;

    if (!cell || !cellsToValidate) {
      return;
    }

    const { alias, rowNumber } = cell;

    // Validate security belongs to entity
    if (alias === CashFlowColumnAlias.ALL_SECURITY || alias === CashFlowColumnAlias.ENTITY) {
      const { current: currentSecurityOptions } = securityOptionsByEntityRef;

      if (!currentSecurityOptions || Object.keys(currentSecurityOptions).length === 0) {
        return;
      }

      if (!rowNumber || rowNumber === 1) {
        return;
      }

      const entityCell = cellsToValidate.find(
        cellToCheck => cellToCheck.alias === CashFlowColumnAlias.ENTITY && cellToCheck.rowNumber === rowNumber
      );
      const securityCell = cellsToValidate.find(
        cellToCheck => cellToCheck.alias === CashFlowColumnAlias.ALL_SECURITY && cellToCheck.rowNumber === rowNumber
      );

      if (!entityCell || !securityCell) {
        return;
      }

      const { value: entityValue } = entityCell;
      const { value: securityValue } = securityCell;

      const entityId = typeof entityValue === 'string' && entityValue.startsWith('company-')
        ? Number(entityValue.replace('company-', ''))
        : null;

      let securityId = null;
      if (typeof securityValue === 'number') {
        securityId = securityValue;
      } else if (typeof securityValue === 'string') {
        securityId = Number.parseInt(securityValue, 10);
      }

      if (!entityId || !securityId) {
        removeFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
        return;
      }

      const validSecurityOptions = currentSecurityOptions[entityId] ?? [];
      const securityIsValid = validSecurityOptions.some(({ value }) => Number(value) === securityId);

      if (securityIsValid) {
        removeFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
      } else {
        addFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
      }
    }
  }, []);

  useEffect(() => {
    if (!ledgerData) {
      return;
    }

    setSpreadsheet(
      new SpreadsheetConfig({
        columns,
        name: 'CashDistributions',
        parser: CashFlowParser,
        reverseParser: CashFlowReverseParser,
        rowConfig,
        format: '',
        customValidations,
        allowConfirmAndDeleteRow: !isDisabled,
        allowAddRow: !isDisabled,
        allowAddMultipleRows: !isDisabled,
        allowCopyRows: !isDisabled,
        displayDeleteRowBtn: !isDisabled,
        deleteRowFn: onDeleteRow || (() => { }),
        showToolbar: false,
        showTitlesColumn: false,
        showFirstRowAsTitle: false,
        currencyFormatter: false,
        unitsFormatter: false,
      })
    );
    // customValidations and onDeleteRow are intentionally omitted from deps - they're stable with useCallback([])
    // Including them would cause unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerData, columns, rowConfig, isDisabled, onDeleteRow]);

  return {
    spreadsheet,
  };
};
